import React from 'react';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Container, Typography } from '@mui/material';
import ThinWarning from '~/components/icons/ThinWarning.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import { AppStatusChip } from '~/modules/covenants/CovenantsDataApplicationForm/components/AppStatusChip.tsx';
import { DocList } from '~/modules/covenants/CovenantsDataApplicationForm/components/DocList.tsx';
import {
  TApiApplicationDetails,
  TApiApplicationDocument,
  TLocalFileEntry,
  TPageConfiguration,
  TSignatureValidationState,
  TUseLocalFilesResult,
} from '~/modules/covenants/types.ts';
import { COMPLETED_STATUSES, READ_ONLY_STATUSES } from '~/modules/covenants/constants.tsx';
import { APPLICATION_FORM_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';
import {
  getApplicationFormCreatedAtLabel,
  getApplicationFormTitle,
} from '~/modules/covenants/CovenantsDataApplicationForm/utils.ts';
import { selectApplicationDocumentsStatus } from '~/store/covenants/selectors.ts';
import { FetchStatus } from '~/store/covenants/types.ts';

type TApplicationFormViewProps = {
  contragentId: number;
  application: TApiApplicationDetails | null;
  isSubmitting: boolean;
  applicationDocuments: TApiApplicationDocument[];
  visibleApplicationDocuments: TApiApplicationDocument[];
  documentsPage: number;
  documentsRowsPerPage: number;
  documentsPageConfiguration: TPageConfiguration;
  signatureValidationState: TSignatureValidationState;
  localFilesController: TUseLocalFilesResult;
  hasApplicationDocuments: boolean;
  hasAnyFiles: boolean;
  needsSign: boolean;
  onPreviewDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onDownloadDocument: (doc: TApiApplicationDocument, localFile?: TLocalFileEntry) => void;
  onDocumentsPageChange: (page: number) => void;
  onDocumentsRowsPerPageChange: (rowsPerPage: number) => void;
  onBulkSignClick: () => void;
  onNoDocuments: () => void;
  onSubmitApplication: () => void;
  onNavigateToList: () => void;
  onRecreateFromDenied: () => void;
};

export const ApplicationFormView: React.FC<TApplicationFormViewProps> = ({
  contragentId,
  application,
  isSubmitting,
  applicationDocuments,
  visibleApplicationDocuments,
  documentsPage,
  documentsRowsPerPage,
  documentsPageConfiguration,
  signatureValidationState,
  localFilesController,
  hasApplicationDocuments,
  hasAnyFiles,
  needsSign,
  onPreviewDocument,
  onDownloadDocument,
  onDocumentsPageChange,
  onDocumentsRowsPerPageChange,
  onBulkSignClick,
  onNoDocuments,
  onSubmitApplication,
  onNavigateToList,
  onRecreateFromDenied,
}) => {
  const {
    localFiles,
    fileInputRef,
    sigFileInputRef,
    handleFileChange,
    handleSignatureFileChange,
    handleUploadForDoc,
    handleUploadSignatureClick,
    handleDeleteDocument,
    handleDeleteSignature,
    handleAddDocumentRow,
    documentGenerationCallbacks,
  } = localFilesController;
  const documentsStatus = useSelector(selectApplicationDocumentsStatus);
  const isDocumentsLoading = documentsStatus === FetchStatus.Loading;
  const isNewApplication = application?.status === 'NEW';
  const isReadOnly = !!application && READ_ONLY_STATUSES.includes(application.status);
  const isInitialDocumentsLoading =
    isDocumentsLoading &&
    !applicationDocuments.length &&
    documentsPageConfiguration.totalElements === 0;
  const isLoading = !application || isInitialDocumentsLoading;

  if (isLoading || !application) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  const isCompleted = COMPLETED_STATUSES.includes(application.status);
  const effectiveReadOnly = isReadOnly;
  const rejectedDocsCount = applicationDocuments.filter((doc) => doc.status === 'DENIED').length;
  const applicationTitle = getApplicationFormTitle(application);
  const createdAtLabel = getApplicationFormCreatedAtLabel(application.createdAt);
  const submitButtonLabel = isCompleted
    ? APPLICATION_FORM_LABELS.done
    : effectiveReadOnly
      ? `${APPLICATION_FORM_LABELS.recreateRejected} (${rejectedDocsCount})`
      : needsSign
        ? APPLICATION_FORM_LABELS.signAndSubmit
        : APPLICATION_FORM_LABELS.submit;

  const shouldShowSignatureNotice = isNewApplication && hasAnyFiles;

  const handleMainActionClick = () => {
    if (!hasApplicationDocuments) {
      onNoDocuments();
      return;
    }

    if (!hasAnyFiles || needsSign) {
      onBulkSignClick();
      return;
    }

    onSubmitApplication();
  };

  return (
    <Box sx={appFormStyles.pageRoot}>
      <Container maxWidth="xl">
        <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} />
        <input
          type="file"
          hidden
          ref={sigFileInputRef}
          onChange={handleSignatureFileChange}
          accept=".p7s"
        />

        <Box sx={appFormStyles.paper}>
          <Box sx={commonStyles.backRow}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={onNavigateToList}
              sx={commonStyles.backButton}
            >
              {APPLICATION_FORM_LABELS.back}
            </Button>
          </Box>

          <Box sx={appFormStyles.header}>
            <Typography variant="h4" sx={appFormStyles.title}>
              {applicationTitle}
            </Typography>

            {shouldShowSignatureNotice && (
              <Box sx={appFormStyles.signatureNotice}>
                <ThinWarning sx={appFormStyles.signatureNoticeIcon} />
                <Box sx={appFormStyles.signatureNoticeContent}>
                  <Typography sx={appFormStyles.signatureNoticeTitle}>
                    {APPLICATION_FORM_LABELS.signatureNoticeTitle}
                  </Typography>
                  <Typography component="div" sx={appFormStyles.signatureNoticeInstruction}>
                    {APPLICATION_FORM_LABELS.signatureNoticeBeforeMenuIcon}
                  </Typography>
                  <Typography sx={appFormStyles.signatureNoticeAllowedTypes}>
                    {APPLICATION_FORM_LABELS.signatureNoticeAllowedSignatureTypes}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={appFormStyles.metaRow}>
              <Box sx={appFormStyles.status}>
                <AppStatusChip status={application.status} />
              </Box>

              {createdAtLabel && (
                <Typography variant="body2" sx={appFormStyles.createdAt}>
                  {createdAtLabel}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={appFormStyles.docsPaper}>
            <DocList
              contragentId={contragentId}
              applicationStatus={application.status}
              isNewApplication={isNewApplication}
              applicationDocuments={visibleApplicationDocuments}
              page={documentsPage}
              rowsPerPage={documentsRowsPerPage}
              pageConfiguration={documentsPageConfiguration}
              isLoading={isDocumentsLoading}
              showDeadlineColumn
              localFiles={localFiles}
              signatureValidationState={signatureValidationState}
              isReadOnly={effectiveReadOnly}
              onUploadForDoc={handleUploadForDoc}
              onUploadSignatureClick={handleUploadSignatureClick}
              onDeleteDocument={handleDeleteDocument}
              onDeleteSignature={handleDeleteSignature}
              onPreviewDocument={onPreviewDocument}
              onDownloadDocument={onDownloadDocument}
              onAddDocumentRow={handleAddDocumentRow}
              documentGenerationCallbacks={documentGenerationCallbacks}
              onPageChange={onDocumentsPageChange}
              onRowsPerPageChange={onDocumentsRowsPerPageChange}
            />
          </Box>

          <Box sx={appFormStyles.footer}>
            <Button
              variant="contained"
              size="large"
              color={isCompleted ? 'primary' : effectiveReadOnly ? 'warning' : 'primary'}
              onClick={
                isCompleted
                  ? onNavigateToList
                  : effectiveReadOnly
                    ? onRecreateFromDenied
                    : handleMainActionClick
              }
              disabled={isSubmitting || (effectiveReadOnly && rejectedDocsCount === 0)}
              sx={appFormStyles.submit}
            >
              {submitButtonLabel}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
